import * as React from 'react';
import { Box, Typography, Container, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import {
  HiPlay,
  HiSearch,
  HiBookmark,
  HiCollection,
  HiDeviceMobile,
  HiLightningBolt,
} from 'react-icons/hi';

const features = [
  {
    icon: <HiPlay size={28} />,
    title: 'HD Streaming',
    description: 'Crystal-clear playback with adaptive bitrate for smooth streaming on any connection.',
    gradient: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
  },
  {
    icon: <HiSearch size={28} />,
    title: 'Smart Search',
    description: 'Find any anime instantly with powerful filters by genre, year, rating, and more.',
    gradient: 'linear-gradient(135deg, #00B4D8, #0090B0)',
  },
  {
    icon: <HiBookmark size={28} />,
    title: 'Watchlists',
    description: 'Save your favorites, track what you are watching, and never lose your place.',
    gradient: 'linear-gradient(135deg, #EC4899, #DB2777)',
  },
  {
    icon: <HiCollection size={28} />,
    title: 'Vast Library',
    description: 'Over 10,000 anime series and movies from every genre imaginable.',
    gradient: 'linear-gradient(135deg, #10B981, #059669)',
  },
  {
    icon: <HiDeviceMobile size={28} />,
    title: 'Cross-Platform',
    description: 'Watch on your TV, laptop, tablet, or phone. Seamlessly pick up where you left off.',
    gradient: 'linear-gradient(135deg, #F59E0B, #D97706)',
  },
  {
    icon: <HiLightningBolt size={28} />,
    title: 'Lightning Fast',
    description: 'Optimized for performance with instant page loads and buttery-smooth navigation.',
    gradient: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.175, 0.885, 0.32, 1.275] },
  },
};

export default function FeaturesSection() {
  return (
    <Box sx={{ position: 'relative', py: { xs: 10, md: 16 }, background: '#050505' }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              sx={{
                display: 'inline-flex',
                px: 2,
                py: 0.5,
                borderRadius: 100,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#8B5CF6',
                background: 'rgba(139,92,246,0.1)',
                border: '1px solid rgba(139,92,246,0.2)',
                mb: 2,
              }}
            >
              Everything You Need
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                mb: 2,
              }}
            >
              Built for{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #8B5CF6, #00B4D8)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                anime lovers
              </Box>
            </Typography>
            <Typography
              sx={{
                color: '#94A3B8',
                fontSize: { xs: '1rem', md: '1.125rem' },
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Every feature designed to give you the best anime streaming experience possible.
            </Typography>
          </Box>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 2, md: 3 },
            }}
          >
            {features.map((feature) => (
              <motion.div key={feature.title} variants={itemVariants}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    height: '100%',
                    '&:hover': {
                      background: 'rgba(255,255,255,0.06)',
                      borderColor: 'rgba(139,92,246,0.2)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: feature.gradient,
                      mb: 2.5,
                      color: '#fff',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 1, color: '#F8FAFC' }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#94A3B8', lineHeight: 1.7 }}>
                    {feature.description}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}
